import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StarIcon as Star } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError, formatDate } from "@/lib/utils-app";

export default function BreederReviews({ breederUserId, currentUserId }) {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reviews/breeders/${breederUserId}`);
      setData(r.data);
    } catch {/* ignore */}
    setLoading(false);
  };
  useEffect(() => { if (breederUserId) load(); }, [breederUserId]);

  // When the page is opened with #reviews (e.g. from /escrow/my "Leave a
  // review" CTA), scroll the section into view AND auto-open the form so
  // the buyer lands directly on the composer.
  useEffect(() => {
    if (loading) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#reviews") return;
    if (!currentUserId || currentUserId === breederUserId) return;
    setShowForm(true);
    requestAnimationFrame(() => {
      const el = document.getElementById("reviews");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/breeders/${breederUserId}`, { rating, title, body });
      toast.success("Review submitted");
      setShowForm(false);
      setTitle("");
      setBody("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (loading || !breederUserId) return null;

  return (
    <section id="reviews" className="bg-white border border-[#E5E5E5] rounded-xl p-6 mb-6 scroll-mt-24" data-testid="breeder-reviews-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h2 className="font-spectral text-2xl tracking-tight">Buyer reviews</h2>
          {data.count > 0 && (
            <div className="flex items-center gap-1.5 bg-[#FBF7EC] border border-[#C9A227]/40 rounded-full px-3 py-1">
              <Star size={14} weight="fill" className="text-gold" />
              <span className="text-sm font-bold">{data.average.toFixed(1)}</span>
              <span className="text-xs text-[#6B6B6B]">({data.count})</span>
            </div>
          )}
        </div>
        {currentUserId && currentUserId !== breederUserId && (
          <Button
            onClick={() => setShowForm(!showForm)}
            variant="outline"
            size="sm"
            className="rounded-full border-[#E5E5E5]"
            data-testid="leave-review-btn"
          >
            {showForm ? "Cancel" : "Leave a review"}
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-4 mb-4">
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                data-testid={`review-star-${n}`}
                type="button"
              >
                <Star size={28} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-[#C9A227]" : "text-[#E5E5E5]"} />
              </button>
            ))}
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Headline (optional)"
            className="w-full px-3 py-2 border border-[#E5E5E5] rounded mb-2 text-sm"
            data-testid="review-title-input"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share details — temperament, breeder communication, follow-up support…"
            rows={4}
            data-testid="review-body-input"
          />
          <Button
            onClick={submit}
            disabled={busy}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white mt-3"
            data-testid="review-submit-btn"
          >
            {busy ? "Posting…" : "Post review"}
          </Button>
          <p className="text-xs text-[#6B6B6B] mt-2">Reviews are reserved for buyers who completed a purchase via secure escrow.</p>
        </div>
      )}

      {data.reviews.length === 0 ? (
        <p className="text-sm text-[#6B6B6B]">No reviews yet — be the first.</p>
      ) : (
        <div className="space-y-4">
          {data.reviews.map((r) => (
            <ReviewRow
              key={r.id}
              r={r}
              canReply={currentUserId === breederUserId}
              onReplyUpdated={load}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Single review row + breeder right-of-reply UI.
 *
 * The reviewed breeder (and only them) sees a "Respond" CTA when
 * no reply has been posted, or "Edit response" / "Remove" when one
 * is in place. Anyone else just reads the response inline.
 */
function ReviewRow({ r, canReply, onReplyUpdated }) {
  const [editing, setEditing] = useState(false);
  const [reply, setReply] = useState(r.breeder_reply?.body || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reply.trim()) {
      toast.error("Write a response first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/reviews/${r.id}/reply`, { body: reply.trim() });
      toast.success("Response posted.");
      setEditing(false);
      onReplyUpdated && onReplyUpdated();
    } catch (e) {
      // Content-safety pre-check returns 400 with a structured detail.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "content_safety") {
        const proceed = window.confirm(
          `Warning — ${detail.severity.toUpperCase()}\n\n${detail.message}\n\n` +
            "Click OK to publish anyway (Trust & Safety may still review it), or Cancel to revise.",
        );
        if (proceed) {
          try {
            await api.post(`/reviews/${r.id}/reply`, {
              body: reply.trim(),
              ack_warning: true,
            });
            toast.success("Response posted.");
            setEditing(false);
            onReplyUpdated && onReplyUpdated();
            return;
          } catch (e2) {
            toast.error(formatApiError(e2));
          }
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/reviews/${r.id}/reply`);
      toast.success("Response removed.");
      setReply("");
      onReplyUpdated && onReplyUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-[#E5E5E5] pt-3" data-testid={`review-${r.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="flex">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={14} weight={n <= r.rating ? "fill" : "regular"} className={n <= r.rating ? "text-[#C9A227]" : "text-[#E5E5E5]"} />
          ))}
        </div>
        <span className="text-xs text-[#6B6B6B]">by {r.buyer_name || "Buyer"} · {formatDate(r.created_at)}</span>
        {r.verified && <span className="text-[10px] uppercase tracking-wider text-green-700 font-bold">Verified</span>}
      </div>
      {r.title && <h4 className="font-medium text-sm">{r.title}</h4>}
      {r.body && <p className="text-sm text-ink mt-1">{r.body}</p>}

      {/* Breeder response (right of reply) */}
      {r.breeder_reply && !editing && (
        <div
          className="mt-3 ml-4 border-l-2 border-[#C9A227]/60 pl-3 py-1 bg-[#FBF7EC]/50 rounded-r"
          data-testid={`review-reply-${r.id}`}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-0.5">
            Breeder response · {formatDate(r.breeder_reply.created_at)}
          </div>
          <p className="text-sm text-ink whitespace-pre-line">{r.breeder_reply.body}</p>
          {canReply && (
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs font-semibold text-crimson hover:underline"
                data-testid={`review-reply-edit-${r.id}`}
              >
                Edit response
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="text-xs font-semibold text-[#6B6B6B] hover:text-crimson"
                data-testid={`review-reply-remove-${r.id}`}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      )}

      {canReply && (editing || !r.breeder_reply) && (
        <div className="mt-3 ml-4 pl-3 border-l-2 border-dashed border-[#C9A227]/60">
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-bold uppercase tracking-wider text-crimson hover:underline"
              data-testid={`review-reply-open-${r.id}`}
            >
              Respond to this review
            </button>
          ) : (
            <>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Thanks for the kind words — and to anyone reading, this is what we strive for…"
                rows={3}
                className="text-sm"
                data-testid={`review-reply-textarea-${r.id}`}
              />
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  onClick={save}
                  disabled={busy}
                  className="bg-ink hover:bg-black text-white text-xs"
                  data-testid={`review-reply-save-${r.id}`}
                >
                  {busy ? "Posting…" : r.breeder_reply ? "Save changes" : "Post response"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setReply(r.breeder_reply?.body || "");
                  }}
                  className="text-xs"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
